/**
 * usePageMeta — keeps `<title>` and `<meta name="description">` in sync
 * with the current page + active locale (Iter 67ea).
 *
 * Usage:
 *   usePageMeta("meta.browse.title", "meta.browse.description");
 *   usePageMeta("meta.carDetail.title", "meta.carDetail.description",
 *               { car: car?.title });
 *
 * - Title is composed as `${t(titleKey, params)} | ${t("meta.siteSuffix")}`
 *   except for the home page where the suffix already lives in the title.
 * - Description is set on the existing `<meta name="description">` tag.
 *   If it's missing we create it.
 * - Re-runs when the locale or params change so language switches paint
 *   the new strings without a refresh on subsequent SPA navigations.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

function setMetaContent(name, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setOgContent(prop, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export default function usePageMeta(titleKey, descKey, params) {
  const { t, i18n } = useTranslation();
  // Stringify params so the deps array compares by value, not reference.
  const paramsKey = params ? JSON.stringify(params) : "";

  useEffect(() => {
    const suffix = t("meta.siteSuffix", "RentalCar.al");
    const titleRaw = titleKey ? t(titleKey, params) : "";
    const description = descKey ? t(descKey, params) : "";

    // Compose: "Page Title | RentalCar.al" unless the page title is
    // already the brand or a marketing tagline that mentions it.
    const composed = titleRaw
      ? (titleRaw.toLowerCase().includes("rentalcar") ? titleRaw : `${titleRaw} | ${suffix}`)
      : suffix;

    document.title = composed;
    setMetaContent("description", description);
    // Keep OG/Twitter in sync so social shares from any page render the
    // localized title + description preview.
    setOgContent("og:title", composed);
    setOgContent("og:description", description);
    setMetaContent("twitter:title", composed);
    setMetaContent("twitter:description", description);
    // i18n.language as a dep ensures language switches re-run the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleKey, descKey, paramsKey, i18n.language]);
}
